// SPDX-License-Identifier: GPL-3.0-or-later
// Stream background: one muted live-stream frame, only while the Twitch channel is live.
//   "twitch": a panel in the free area beside the table (stream-panel.ts). It shows only on a wide
//             screen with a mouse. On any other screen the choice shows nothing, because Twitch
//             refuses to autoplay a covered frame.
//   "off":    no frame.
// An old saved "youtube" value loads as the default choice (see `normalizeSource`).
// While the channel is not live, or the source is "off", there is no frame at all, so the page makes
// no request to a player.
// The module keeps no choice of its own. The settings store (`streamBackground`) is the one place
// that saves the choice: the page reads it and calls `setSource`. The live state comes from
// `setLive` (see live-status.ts).
// The game page uses this module. The /overlay page must never import it.

import {
  DEFAULT_STREAM_SOURCE,
  normalizeSource,
  type PanelBox,
  panelLayout,
  resolveSource,
  type StreamSource,
  sourceUrl,
} from "./stream-config.ts";
import { createPanel, type Panel, type StreamPlayfield } from "./stream-panel.ts";

/** The part of an element that the layer uses. A real element fits it. */
export interface StreamNode {
  className: string;
  readonly style: { setProperty(name: string, value: string): void };
  readonly ownerDocument: StreamDocument;
  setAttribute(name: string, value: string): void;
  append(...nodes: StreamNode[]): void;
  remove(): void;
}

/** The part of a document that the layer uses. */
export interface StreamDocument {
  createElement(tag: string): StreamNode;
  /** Optional. The layer finds the playfield with it when the page gives none. */
  querySelector?(selector: string): StreamPlayfield | null;
  /** Optional. The layer hears the `close` of a dialog with it (the event does not bubble). */
  addEventListener?(type: string, listener: () => void, capture?: boolean): void;
  removeEventListener?(type: string, listener: () => void, capture?: boolean): void;
}

/** The part of `window` that the layer uses to size the frame. It is optional. */
export interface StreamWindow {
  readonly innerWidth: number;
  readonly innerHeight: number;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
  /** Optional. A screen with a coarse pointer (touch) gets no Twitch panel. */
  matchMedia?(query: string): { readonly matches: boolean };
}

/** What the layer takes from the page. */
export interface StreamBackgroundDeps {
  /** The element that gets the layer. The game page passes `document.body`. */
  readonly host: Pick<StreamNode, "ownerDocument" | "append">;
  /** Only the host name is used. It is the `parent` of the Twitch player. */
  readonly location: { readonly hostname: string };
  /** Gives the size of the viewport. Without it there is no Twitch panel. */
  readonly win?: StreamWindow | null;
  /**
   * The source to show at the start. The page passes the choice of the settings store, so a player
   * who chose "off" never loads a frame. A value that is no choice (an old "youtube") gives the
   * default source. Default: the default source.
   */
  readonly initial?: unknown;
  /** True when the channel is live at the start. Default: false (no frame until `setLive(true)`). */
  readonly initialLive?: boolean;
  /** The element that holds the Twitch panel. Default: the element with `data-slot="playfield"`. */
  readonly playfield?: StreamPlayfield | null;
}

/** The handle that the page keeps. */
export interface StreamBackground {
  /**
   * Choose a source. A value that is no choice gives the default. The result is the source that is
   * shown (it is "off" while the channel is not live).
   */
  setSource(next: unknown): StreamSource;
  /** Tell the layer if the channel is live. The result is the source that is shown. */
  setLive(live: boolean): StreamSource;
  /** The source that is shown now. */
  getSource(): StreamSource;
  /** Remove the panel, the frame and the listeners. */
  dispose(): void;
}

const FRAME_ALLOW = "autoplay; encrypted-media; picture-in-picture";
const FRAME_REFERRER_POLICY = "strict-origin-when-cross-origin";
const RESIZE_EVENTS: readonly string[] = ["resize", "orientationchange"];
const PLAYFIELD_SELECTOR = '[data-slot="playfield"]';
const COARSE_POINTER = "(pointer: coarse)";
// A modal dialog paints a backdrop over the whole page. Twitch refuses to autoplay a covered frame.
const OPEN_DIALOG = "dialog[open]";
const DIALOG_CLOSE = "close";
const TWITCH_TITLE = "Live stream (Twitch)";

/** Where the Twitch panel goes. */
interface Spot {
  readonly field: StreamPlayfield;
  readonly box: PanelBox;
}

/** What to show: the source that is shown, and the spot when it is the Twitch panel. */
interface Plan {
  readonly source: StreamSource;
  readonly spot: Spot | null;
}

const NOTHING: Plan = { source: "off", spot: null };

/** The first source: the one that the page gives, else the default. An old or bad value gives the default. */
function firstChoice(deps: StreamBackgroundDeps): StreamSource {
  return deps.initial === undefined ? DEFAULT_STREAM_SOURCE : normalizeSource(deps.initial);
}

/** Make the frame. It is hidden from the keyboard, the pointer and screen readers. */
function makeFrame(doc: StreamDocument, url: string): StreamNode {
  const frame = doc.createElement("iframe");
  frame.className = "stream-panel__frame";
  frame.setAttribute("src", url);
  frame.setAttribute("title", TWITCH_TITLE);
  frame.setAttribute("allow", FRAME_ALLOW);
  frame.setAttribute("referrerpolicy", FRAME_REFERRER_POLICY);
  frame.setAttribute("loading", "eager");
  frame.setAttribute("tabindex", "-1");
  frame.setAttribute("aria-hidden", "true");
  return frame;
}

/** Find the free area for the Twitch panel, or null when there is none. */
function findSpot(deps: StreamBackgroundDeps, win: StreamWindow | null): Spot | null {
  const field = deps.playfield ?? deps.host.ownerDocument.querySelector?.(PLAYFIELD_SELECTOR);
  if (win === null || field === undefined || field === null) return null;
  if (win.matchMedia?.(COARSE_POINTER).matches === true) return null;
  const rect = field.getBoundingClientRect();
  const box = panelLayout(win.innerWidth, rect.width, rect.height);
  return box === null ? null : { field, box };
}

/** The state of one layer. The helpers below read and write it. */
interface BgState {
  readonly deps: StreamBackgroundDeps;
  readonly win: StreamWindow | null;
  readonly doc: StreamDocument;
  requested: StreamSource;
  live: boolean;
  shown: StreamSource | null;
  panel: Panel | null;
  disposed: boolean;
  onResize: () => void;
  onDialogClose: () => void;
}

function streamBackgroundPlan(s: BgState): Plan {
  const wanted = resolveSource(s.requested, s.deps.location.hostname);
  if (!s.live || wanted !== "twitch") return NOTHING;
  // Hold the Twitch frame back while a modal dialog is open. A panel that is already shown stays.
  if (s.shown !== "twitch" && s.doc.querySelector?.(OPEN_DIALOG)) return NOTHING;
  const spot = findSpot(s.deps, s.win);
  return spot === null ? NOTHING : { source: "twitch", spot };
}

function streamBackgroundClear(s: BgState): void {
  s.panel?.remove();
  s.panel = null;
}

function streamBackgroundShow(s: BgState, next: Plan): void {
  streamBackgroundClear(s);
  const url = sourceUrl(next.source, s.deps.location.hostname);
  if (url === null || next.spot === null) return;
  s.panel = createPanel(s.doc, next.spot.field, makeFrame(s.doc, url), next.spot.box);
}

// A new frame only when the source changes. The same source only moves the panel.
function streamBackgroundApply(s: BgState): StreamSource {
  const next = streamBackgroundPlan(s);
  if (next.source !== s.shown) {
    s.shown = next.source;
    streamBackgroundShow(s, next);
  } else if (next.spot !== null) {
    s.panel?.place(next.spot.box);
  }
  return next.source;
}

function streamBackgroundSetSource(s: BgState, next: unknown): StreamSource {
  if (s.disposed) return s.shown ?? "off";
  s.requested = normalizeSource(next);
  return streamBackgroundApply(s);
}

function streamBackgroundSetLive(s: BgState, next: boolean): StreamSource {
  if (s.disposed) return "off";
  s.live = next === true;
  return streamBackgroundApply(s);
}

function streamBackgroundDispose(s: BgState): void {
  if (s.disposed) return;
  s.disposed = true;
  s.doc.removeEventListener?.(DIALOG_CLOSE, s.onDialogClose, true);
  if (s.win !== null) for (const type of RESIZE_EVENTS) s.win.removeEventListener(type, s.onResize);
  streamBackgroundClear(s);
  s.shown = "off";
}

/** Make the layer. It shows the first source as soon as the channel is live. */
export function createStreamBackground(deps: StreamBackgroundDeps): StreamBackground {
  const { host, win = null } = deps;
  const doc = host.ownerDocument;
  const s: BgState = {
    deps,
    win,
    doc,
    requested: firstChoice(deps),
    live: deps.initialLive === true,
    shown: null,
    panel: null,
    disposed: false,
    onResize: () => {
      if (!s.disposed) streamBackgroundApply(s);
    },
    // The `close` event does not bubble: the capture phase on the document hears each dialog.
    onDialogClose: () => {
      if (!s.disposed) streamBackgroundApply(s);
    },
  };
  doc.addEventListener?.(DIALOG_CLOSE, s.onDialogClose, true);
  if (win !== null) for (const type of RESIZE_EVENTS) win.addEventListener(type, s.onResize);
  streamBackgroundApply(s);
  return {
    setSource: (next) => streamBackgroundSetSource(s, next),
    setLive: (next) => streamBackgroundSetLive(s, next),
    getSource: () => s.shown ?? "off",
    dispose: () => streamBackgroundDispose(s),
  };
}
